import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "sidebarExpandedState";
// Same-tab broadcast: the browser `storage` event only fires in *other* tabs, so
// we dispatch our own event to keep components in the same tab in sync.
const CHANGE_EVENT = "sidebar-expanded-change";

function readExpanded(): boolean {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw !== null ? JSON.parse(raw) : true;
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

/**
 * Reactive replacement for `useLocalStorageState("sidebarExpandedState")`. Every
 * consumer re-renders when the value changes anywhere in the tab, so UI outside
 * the sidebar (e.g. the snackbar) can shift to match the current width.
 */
export function useSidebarExpanded(): [boolean, (value: boolean) => void] {
  const expanded = useSyncExternalStore(subscribe, readExpanded, () => true);

  const setExpanded = useCallback((value: boolean) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }, []);

  return [expanded, setExpanded];
}
